import { useCallback, useEffect, useMemo, useState } from 'react'
import { closeFile, downloadExport, openFile } from './api'
import { META } from './meta'
import { useRowSource } from './useRowSource'
import { ComparePanel } from './components/ComparePanel'
import { DataTable } from './components/DataTable'
import { DuplicatePanel, dupMode } from './components/DuplicatePanel'
import { EmailCheckPanel, suggestEmailColumn } from './components/EmailCheckPanel'
import { InsightsPanel, suggestInsightColumns } from './components/InsightsPanel'
import { InsightsView } from './components/InsightsView'
import { ExportPanel } from './components/ExportPanel'
import { FilterPanel } from './components/FilterPanel'
import { FilePicker } from './components/FilePicker'
import { SavedViews } from './components/SavedViews'
import { ScopeTrail } from './components/ScopeTrail'
import { TableStats } from './components/TableStats'
import { pushRecent, rememberHandle } from './handles'
import { useInsights } from './useInsights'
import { bytes, n } from './format'
import { countRules, emptySpec, isGroup, newId, reconcileSpec, specSignature } from './filterSpec'
import {
  describeResolution,
  emptyResolution,
  reconcileResolution,
  toRequestResolution,
} from './resolveSpec'
import { compareMode, emptyCompare, initialCompare } from './compareSpec'

const EMPTY = emptySpec()

/**
 * Restore a saved view against the file as it is now. A file can gain or lose
 * columns between saves, so anything referring to a column that no longer exists
 * is dropped rather than sent to the engine, which would reject the whole query.
 * Filter groups survive; one left empty by a dropped rule goes with it.
 */
function reconcileView(view, headers, preferences) {
  const known = new Set(headers)
  const { spec, dropped } = reconcileSpec(view.filters, headers)
  const dupColumns = (view.dupColumns ?? []).filter((c) => known.has(c))
  const dupDropped = (view.dupColumns ?? []).length - dupColumns.length
  const { resolution, dropped: ruleDropped } = reconcileResolution(view.dupResolution, headers, preferences)
  const insightColumns = (view.insightColumns ?? []).filter((c) => known.has(c))
  const insightDropped = (view.insightColumns ?? []).length - insightColumns.length
  // An added column whose name the file has since grown is dropped: the file's
  // own column wins, and an export must never hold two columns with one name.
  const lower = new Set(headers.map((h) => h.toLowerCase()))
  const customColumns = (view.customColumns ?? []).filter((c) => !lower.has(c.name.toLowerCase()))
  const customDropped = (view.customColumns ?? []).length - customColumns.length

  const missing = []
  if (dropped > 0) missing.push(`${dropped} filter${dropped === 1 ? '' : 's'}`)
  if (dupDropped > 0) missing.push('the duplicate key')
  if (ruleDropped > 0) missing.push(`${ruleDropped} resolution rule${ruleDropped === 1 ? '' : 's'}`)
  if (insightDropped > 0) missing.push(`${insightDropped} insight column${insightDropped === 1 ? '' : 's'}`)
  if (customDropped > 0) {
    missing.push(`${customDropped} added column${customDropped === 1 ? '' : 's'} (the file now has that name)`)
  }

  const dupUsable = dupDropped === 0 && dupColumns.length > 0
  return {
    filters: spec,
    resolution,
    customColumns,
    // The key plus what the view did with it: show the duplicates, hide them, or
    // list what hiding them removes — and the rules that decide which copy stays.
    dup: dupUsable
      ? {
          columns: dupColumns,
          mode: dupMode(view.dupMode).id,
          resolution: toRequestResolution(resolution, preferences),
        }
      : null,
    hidden: new Set((view.hiddenColumns ?? []).filter((c) => known.has(c))),
    insightColumns: insightColumns.length > 0 ? insightColumns : null,
    insightCombine: !!view.insightCombine,
    warning: missing.length ? `Skipped ${missing.join(' and ')} — those columns are not in this file.` : null,
  }
}

/** Column width from the sampled max cell length — good enough, and free. */
const widthFor = (col) => Math.max(90, Math.min(340, col.maxLen * 7.6 + 26))

const MAX_CUSTOM_COLUMNS = 8

function ColumnsPanel({ columns, hidden, setHidden, custom, setCustom }) {
  const [search, setSearch] = useState('')
  const [newName, setNewName] = useState('')
  const [newValue, setNewValue] = useState('')
  const shown = columns.length - hidden.size
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? columns.filter((c) => c.name.toLowerCase().includes(q)) : columns
  }, [columns, search])

  // A name the file (or this list) already uses would put two columns with one
  // name in the export, so it cannot be added.
  const trimmedName = newName.trim()
  const clash =
    trimmedName !== '' &&
    [...columns.map((c) => c.name), ...custom.map((c) => c.name)].some(
      (name) => name.toLowerCase() === trimmedName.toLowerCase(),
    )
  const addColumn = () => {
    if (!trimmedName || clash) return
    setCustom([...custom, { id: newId(), name: trimmedName, value: newValue }])
    setNewName('')
    setNewValue('')
  }

  return (
    <section className="panel">
      <header className="panel-head">
        <h2>Columns</h2>
        <span className="count">
          {shown} of {columns.length} shown
        </span>
      </header>
      <p className="hint">Untick a column to hide it here and leave it out of an export.</p>
      <div className="panel-actions tight">
        <button className="btn tiny" onClick={() => setHidden(new Set())}>
          Show all
        </button>
        <button
          className="btn tiny"
          title="Leaves the first column visible so the table still has something to show"
          onClick={() => setHidden(new Set(columns.slice(1).map((c) => c.name)))}
        >
          Hide all
        </button>
      </div>
      {columns.length > 10 && (
        <input
          className="input tiny"
          placeholder="Find column…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      <div className="value-options column-list">
        {visible.map((c) => (
          <label key={c.name} className="value-option">
            <input
              type="checkbox"
              checked={!hidden.has(c.name)}
              onChange={(e) => {
                const next = new Set(hidden)
                if (e.target.checked) next.delete(c.name)
                else next.add(c.name)
                setHidden(next)
              }}
            />
            <span className="value-text" title={`${c.name} · ${c.kind}`}>
              {c.name}
            </span>
            {c.kind === 'number' && <span className="value-count">123</span>}
          </label>
        ))}
        {visible.length === 0 && <span className="muted">No matching column.</span>}
      </div>

      <hr className="panel-rule" />
      <p className="hint">
        Add a column of your own — every row shows, and every export writes, the same value. Good for
        tagging a list before it goes somewhere: <em>Source = August warm-up</em>.
      </p>
      {custom.length > 0 && (
        <div className="custom-columns">
          {custom.map((c) => (
            <div key={c.id} className="custom-column" title={`${c.name} = ${c.value || '(empty)'}`}>
              <span className="value-text">
                <strong>{c.name}</strong> = {c.value === '' ? <span className="muted">(empty)</span> : c.value}
              </span>
              <button
                className="icon-btn"
                title="Remove this added column"
                onClick={() => setCustom(custom.filter((x) => x.id !== c.id))}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      {custom.length < MAX_CUSTOM_COLUMNS && (
        <>
          <div className="value-row">
            <input
              className="input"
              placeholder="Column name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addColumn()}
            />
            <input
              className="input"
              placeholder="Value for every row"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addColumn()}
            />
          </div>
          {clash && <p className="footnote warn-text">A column called “{trimmedName}” already exists.</p>}
          <div className="panel-actions tight">
            <button className="btn" disabled={!trimmedName || clash} onClick={addColumn}>
              + Add column
            </button>
          </div>
        </>
      )}
    </section>
  )
}

/** The tool rail: every function one click away, and never more than one open. */
const TOOLS = [
  { id: 'filters', label: 'Filters', icon: '▽' },
  { id: 'duplicates', label: 'Duplicates', icon: '⧉' },
  { id: 'emails', label: 'Emails', icon: '@' },
  { id: 'compare', label: 'Compare', icon: '⇄' },
  { id: 'insights', label: 'Insights', icon: '▤' },
  { id: 'columns', label: 'Columns', icon: '▦' },
  { id: 'export', label: 'Export', icon: '↓' },
  { id: 'views', label: 'Views', icon: '★' },
]

/** The two ways a comparison can be written out, offered in the Export panel. */
const COMPARE_SHAPES = [
  {
    id: 'rows',
    label: 'A row per record',
    blurb: 'The verdict, both row numbers, the columns that differ, and the row itself.',
  },
  {
    id: 'cells',
    label: 'A row per changed value',
    blurb: 'One line per differing cell: the key, the column, and what each side says.',
  },
]

export default function App() {
  const meta = META
  const [dataset, setDataset] = useState(null)
  const [opening, setOpening] = useState(false)
  const [openError, setOpenError] = useState(null)
  const [tool, setTool] = useState('filters')
  const [draft, setDraft] = useState(EMPTY)
  const [applied, setApplied] = useState(EMPTY)
  const [hidden, setHidden] = useState(new Set())
  // Columns the user adds by hand: a name and the one value every row gets.
  const [customColumns, setCustomColumns] = useState([])
  const [dupDraft, setDupDraft] = useState(new Set())
  const [dupModeDraft, setDupModeDraft] = useState('duplicates')
  // The rules that pick which copy of a duplicate group survives, as edited.
  const [resolutionDraft, setResolutionDraft] = useState(emptyResolution)
  // `{columns, mode, resolution}` once a duplicate key is in play, null when
  // browsing every row.
  const [dupApplied, setDupApplied] = useState(null)
  const [viewNote, setViewNote] = useState(null)
  // The saved view this session came from, so it can be updated in place later.
  const [activeView, setActiveView] = useState(null)
  const [insightDraft, setInsightDraft] = useState(new Set())
  const [insightCombine, setInsightCombine] = useState(false)
  const [insightApplied, setInsightApplied] = useState(null)
  // The email domain check: which column it looks at, and whether it has been
  // asked to run. Results live in the engine, so the panel re-polls on remount.
  const [emailColumn, setEmailColumn] = useState(null)
  const [emailStarted, setEmailStarted] = useState(false)
  // The second file to compare against, as an opened dataset of its own.
  const [upload, setUpload] = useState(null)
  const [compareDraft, setCompareDraft] = useState(emptyCompare)
  const [compareModeDraft, setCompareModeDraft] = useState('all')
  // `{other, spec, mode, fileName}` while a comparison is running, null otherwise.
  const [compareApplied, setCompareApplied] = useState(null)
  // The topbar export builds the file in the worker, so it has a busy state.
  const [exporting, setExporting] = useState(false)

  const preferences = meta.preferences

  const applyView = useCallback((view, info, prefs) => {
    const next = reconcileView(view, info.headers, prefs)
    setDraft(next.filters)
    setApplied(next.filters)
    setDupApplied(next.dup)
    setDupDraft(new Set(next.dup?.columns ?? (info.suggestedKey ? [info.suggestedKey] : [])))
    setDupModeDraft(next.dup?.mode ?? 'duplicates')
    setResolutionDraft(next.resolution)
    setHidden(next.hidden)
    setCustomColumns(next.customColumns.map((c) => ({ ...c, id: newId() })))
    setInsightCombine(next.insightCombine)
    setInsightApplied(next.insightColumns ? { columns: next.insightColumns, combine: next.insightCombine } : null)
    if (next.insightColumns) setInsightDraft(new Set(next.insightColumns))
    setActiveView({ id: view.id, name: view.name })
    setViewNote(next.warning ? { kind: 'warn', text: next.warning } : { kind: 'ok', text: `Opened “${view.name}”.` })
  }, [])

  /**
   * @param file the File the user picked (or dropped)
   * @param view optional saved view to restore once the file is open
   * @param handle optional FileSystemFileHandle, kept so the file can be
   *   reopened from the landing page without another picker round
   */
  const open = useCallback(
    async (file, view = null, handle = null) => {
      setOpening(true)
      setOpenError(null)
      setViewNote(null)
      try {
        const info = await openFile(file)
        setDataset(info)
        setTool('filters')
        if (view) {
          applyView(view, info, preferences)
        } else {
          setDraft(EMPTY)
          setApplied(EMPTY)
          setHidden(new Set())
          setCustomColumns([])
          setActiveView(null)
          // Pre-select the likely identity column (email, usually) for duplicate checks.
          setDupDraft(new Set(info.suggestedKey ? [info.suggestedKey] : []))
          setDupModeDraft('duplicates')
          setResolutionDraft(emptyResolution())
          setDupApplied(null)
        }
        setInsightDraft(new Set(suggestInsightColumns(info.columns)))
        setInsightCombine(false)
        setInsightApplied(null)
        setEmailColumn(suggestEmailColumn(info.columns))
        setEmailStarted(false)
        // A comparison is about two specific files; neither the second file nor
        // the column pairing means anything once one of them has been swapped out.
        setUpload(null)
        setCompareDraft(emptyCompare())
        setCompareModeDraft('all')
        setCompareApplied(null)
        pushRecent({ name: info.name, size: info.size, hasHandle: !!handle })
        if (handle) rememberHandle(info.name, handle)
      } catch (e) {
        setOpenError(e.message)
      } finally {
        setOpening(false)
      }
    },
    [applyView, preferences],
  )

  const { status, ensureRange, getRow, rowCount } = useRowSource(
    dataset?.id,
    applied,
    dupApplied,
    compareApplied,
  )

  const scopeLabel = dupApplied
    ? dupMode(dupApplied.mode).scope
    : countRules(applied) > 0
      ? 'filtered rows'
      : 'all rows'
  const { result: insightResult, error: insightError } = useInsights(
    dataset?.id,
    applied,
    dupApplied,
    insightApplied,
  )

  // What the rules currently say, in the shape the engine takes. null when they
  // say nothing the default does not — no rules, keep the first copy in the file.
  const resolutionSpec = useMemo(
    () => toRequestResolution(resolutionDraft, preferences),
    [resolutionDraft, preferences],
  )

  const visible = useMemo(
    () => (dataset ? dataset.columns.filter((c) => !hidden.has(c.name)) : []),
    [dataset, hidden],
  )

  /**
   * What the table draws: the visible file columns, then the added ones. An
   * added column's cells sit past the file's own in the row array, so its
   * `index` points there and the row wrapper below fills it. The comparison
   * has its own layout with a second file's values in play, so added columns
   * sit that view out (exports of a comparison don't carry them either).
   */
  const tableColumns = useMemo(() => {
    if (!dataset || compareApplied || customColumns.length === 0) return visible
    const base = dataset.columns.length
    return [
      ...visible,
      ...customColumns.map((c, k) => ({
        name: c.name,
        kind: 'text',
        index: base + k,
        maxLen: Math.max(c.name.length, c.value.length),
      })),
    ]
  }, [dataset, visible, customColumns, compareApplied])
  const widths = useMemo(() => tableColumns.map(widthFor), [tableColumns])

  /** Rows as the table reads them: the file's cells, then each added value. */
  const getRowView = useCallback(
    (index) => {
      if (!dataset || customColumns.length === 0 || compareApplied) return getRow(index)
      const row = getRow(index)
      if (!row) return row
      const c = [...row.c]
      const base = dataset.columns.length
      customColumns.forEach((col, k) => {
        c[base + k] = col.value
      })
      return { ...row, c }
    },
    [getRow, customColumns, compareApplied, dataset],
  )

  const dirty = useMemo(() => specSignature(draft) !== specSignature(applied), [draft, applied])

  const apply = useCallback(
    (spec) => {
      setViewNote(null)
      setApplied(spec && Array.isArray(spec.rules) ? spec : { ...draft })
    },
    [draft],
  )

  /**
   * Remove one step from the trail. Filters live in two places — what is being
   * edited and what is running — and the trail only ever shows what is running,
   * so dropping a step has to take it out of both or the panel would offer to
   * re-apply the thing just dismissed.
   */
  const dropFilter = useCallback((id) => {
    const strip = (spec) => ({ ...spec, rules: spec.rules.filter((r) => r.id !== id) })
    setViewNote(null)
    setDraft(strip)
    setApplied(strip)
  }, [])

  /**
   * The Emails panel adding (or clearing, with `nodes` empty) its filter for a
   * column. It replaces whatever it added before rather than stacking — the
   * panel's nodes are tagged with the column they were built for — and applies
   * immediately: the panel's whole point is "make the bad rows go away now".
   */
  const addEmailRules = useCallback(
    (column, nodes) => {
      setViewNote(null)
      const next = { ...draft, rules: [...draft.rules.filter((r) => r.emailFor !== column), ...nodes] }
      setDraft(next)
      setApplied(next)
    },
    [draft],
  )

  /**
   * A file has arrived. It replaces whatever was being compared before, and the
   * column pairing is guessed afresh against its headers — a mapping built for
   * the previous file says nothing about this one.
   */
  const onUploaded = useCallback(
    (info) => {
      if (!dataset) return
      setUpload(info)
      setCompareDraft(initialCompare(dataset.headers, info))
      setCompareApplied(null)
      setViewNote(null)
    },
    [dataset],
  )

  const onClearUpload = useCallback(() => {
    setUpload(null)
    setCompareDraft(emptyCompare())
    setCompareApplied(null)
  }, [])

  const runCompare = useCallback(
    (next) => {
      setViewNote(null)
      // Breakdowns count the rows of a scope, and a comparison is a scope they do
      // not know how to count — so rather than show a breakdown of the wrong
      // rows, the two tools take turns.
      setInsightApplied(null)
      setCompareApplied({ ...next, fileName: upload?.name ?? null })
    },
    [upload],
  )

  /**
   * Every comparison mode is a slice of one finished scan, so picking a different
   * one costs nothing and applies immediately — making the user press Update to
   * see the same scan from another angle would only imply it was expensive.
   */
  const pickCompareMode = useCallback((next) => {
    setCompareModeDraft(next)
    setCompareApplied((current) => (current ? { ...current, mode: next } : current))
  }, [])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) apply()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [apply])

  if (!dataset) {
    return <FilePicker onOpen={open} busy={opening} error={openError} />
  }

  const scanning = !status.done
  const columnsForExport = visible.length === dataset.columns.length ? null : visible.map((c) => c.name)
  // Only worth saying when the rules are doing something the default would not.
  const resolutionNote = dupApplied ? describeResolution(dupApplied.resolution, preferences) : null
  // A merged export writes assembled records rather than a subset of the file's
  // own rows, so it says so wherever it is offered.
  const mergingNow =
    !!dupApplied?.resolution?.merge && (dupApplied.mode === 'dedupe' || dupApplied.mode === 'kept')
  const compareInfo = compareApplied ? compareMode(compareApplied.mode) : null
  const exportLabel = compareInfo
    ? compareInfo.exportLabel
    : !dupApplied
      ? 'Export CSV'
      : mergingNow
        ? 'Export merged records'
        : dupMode(dupApplied.mode).exportLabel
  const exportHint = compareInfo
    ? compareInfo.exportHint
    : mergingNow
      ? 'One assembled record per duplicate — values filled in from the other copies'
      : dupApplied
        ? dupMode(dupApplied.mode).exportHint
        : null

  // The added columns, in the shape the export op takes.
  const extraColumns = customColumns.length
    ? customColumns.map(({ name, value }) => ({ name, value }))
    : null

  /**
   * One place that knows how to write the current view's rows as a download.
   * A comparison sits outside the other two, so it carries them along rather
   * than replacing them: what it compared was whatever they produced.
   */
  const exportFor = (batch, shape) =>
    downloadExport({
      id: dataset.id,
      filters: applied,
      dup: dupApplied,
      cmp: compareApplied ? { ...compareApplied, shape } : null,
      columns: columnsForExport,
      batch,
      extra: compareApplied ? null : extraColumns,
      shape,
    })
  // Both of these walk rows their scan already retained, so an export before the
  // scan finishes would be a partial answer — the engine refuses it outright.
  const exportBlocked = ((!!dupApplied || !!compareApplied) && scanning) || exporting
  // Rough per-row size for the "~N MB each" hint; only honest for whole rows.
  const rowBytes =
    !columnsForExport && dataset.indexed && dataset.rows > 0 ? dataset.size / dataset.rows : null

  const topbarExport = async () => {
    if (exportBlocked) return
    setExporting(true)
    try {
      await exportFor(null, 'rows')
    } catch (e) {
      setViewNote({ kind: 'warn', text: e.message })
    } finally {
      setExporting(false)
    }
  }

  const hasEmailRule = (nodes) =>
    nodes.some((r) =>
      isGroup(r) ? hasEmailRule(r.rules) : r.op === 'email_valid' || r.op === 'email_invalid',
    )

  const appliedRules = countRules(applied)
  const badges = {
    filters: appliedRules > 0 ? n(appliedRules) : null,
    duplicates: dupApplied ? '●' : null,
    emails: hasEmailRule(applied.rules ?? []) ? '●' : null,
    compare: compareApplied ? '●' : null,
    insights: insightApplied ? '●' : null,
    columns: hidden.size + customColumns.length > 0 ? n(hidden.size + customColumns.length) : null,
  }

  const panels = {
    filters: (
      <>
        <FilterPanel
          columns={dataset.columns}
          operators={meta.operators}
          draft={draft}
          setDraft={setDraft}
          onApply={apply}
          dirty={dirty}
          applied={applied}
        />
        <p className="panel-note">
          The value lists here are built from the first {n(dataset.columns[0]?.sampleRows ?? 0)} rows.
          The filters themselves always run over the whole file.
        </p>
      </>
    ),
    duplicates: (
      <DuplicatePanel
        columns={dataset.columns}
        draft={dupDraft}
        setDraft={setDupDraft}
        mode={dupModeDraft}
        setMode={setDupModeDraft}
        resolution={resolutionDraft}
        setResolution={setResolutionDraft}
        resolutionSpec={resolutionSpec}
        preferences={preferences}
        tiebreaks={meta.tiebreaks}
        maxResolveRules={meta.maxResolveRules}
        applied={dupApplied}
        onFind={(next) => {
          setViewNote(null)
          setDupApplied(next)
        }}
        onExit={() => setDupApplied(null)}
        scanning={!!dupApplied && !status.done}
      />
    ),
    emails: (
      <EmailCheckPanel
        datasetId={dataset.id}
        columns={dataset.columns}
        column={emailColumn}
        setColumn={setEmailColumn}
        started={emailStarted}
        setStarted={setEmailStarted}
        applied={applied}
        onAddRules={addEmailRules}
      />
    ),
    compare: (
      <ComparePanel
        datasetId={dataset.id}
        columns={dataset.columns}
        blankRules={meta.blankRules}
        upload={upload}
        onUploaded={onUploaded}
        onClearUpload={onClearUpload}
        draft={compareDraft}
        setDraft={setCompareDraft}
        mode={compareModeDraft}
        setMode={pickCompareMode}
        applied={compareApplied}
        onCompare={runCompare}
        onExit={() => setCompareApplied(null)}
        scanning={!!compareApplied && !status.done}
      />
    ),
    insights: (
      <InsightsPanel
        columns={dataset.columns}
        draft={insightDraft}
        setDraft={setInsightDraft}
        combine={insightCombine}
        setCombine={setInsightCombine}
        applied={insightApplied}
        onRun={(cols) => {
          setViewNote(null)
          // See runCompare: breakdowns and comparisons take turns.
          setCompareApplied(null)
          setInsightApplied({ columns: cols, combine: insightCombine })
        }}
        onExit={() => setInsightApplied(null)}
        scanning={!!insightApplied && insightResult != null && !insightResult.done}
        scopeLabel={scopeLabel}
      />
    ),
    columns: (
      <ColumnsPanel
        columns={dataset.columns}
        hidden={hidden}
        setHidden={setHidden}
        custom={customColumns}
        setCustom={setCustomColumns}
      />
    ),
    export: (
      <ExportPanel
        download={exportFor}
        // What the export will actually write: a filtered export re-scans and
        // writes every match, while a duplicate or comparison export walks the
        // rows its scan retained, which is what `available` counts.
        rows={dupApplied || compareApplied ? status.available : status.matched}
        scanning={scanning}
        label={exportLabel}
        estimatedRowBytes={rowBytes}
        // A comparison is the one view whose rows can be written more than one way.
        shapes={compareApplied ? COMPARE_SHAPES : null}
      />
    ),
    views: (
      <SavedViews
        dataset={dataset}
        current={() => ({
          filters: applied,
          dupColumns: dupApplied?.columns ?? null,
          dupMode: dupApplied?.mode ?? 'duplicates',
          dupResolution: dupApplied?.resolution ?? null,
          hiddenColumns: [...hidden],
          customColumns: extraColumns,
          insightColumns: insightApplied?.columns ?? null,
          insightCombine: !!insightApplied?.combine,
          rowCount: status.matched,
          // The server stat()ed the file for these; here the dataset carries them.
          mtimeMs: dataset.mtimeMs,
          fileSize: dataset.size,
        })}
        onApply={(view) => applyView(view, dataset, preferences)}
        active={activeView}
        setActive={setActiveView}
      />
    ),
  }

  return (
    <div className="app">
      <header className="topbar">
        <span className="wordmark">CSV Explorer</span>
        <div className="file">
          <span className="file-name" title={dataset.name}>
            {dataset.name}
          </span>
          <span className="file-meta">
            {bytes(dataset.size)} · {dataset.columns.length} columns · delimiter{' '}
            <code>{dataset.delimiter === '\t' ? '\\t' : dataset.delimiter}</code>
          </span>
        </div>
        <div className="topbar-actions">
          <button
            className={`btn primary ${exportBlocked ? 'disabled' : ''}`}
            disabled={exportBlocked}
            onClick={topbarExport}
            title={
              exportBlocked && !exporting ? 'Ready once the scan finishes' : (exportHint ?? undefined)
            }
          >
            {exporting ? 'Building…' : exportLabel}
          </button>
          <button
            className="btn subtle"
            onClick={() => {
              closeFile(dataset.id).catch(() => {})
              setDataset(null)
            }}
          >
            Open another file
          </button>
        </div>
      </header>

      <div className="body">
        <nav className="rail" aria-label="Tools">
          {TOOLS.map((t) => (
            <button
              key={t.id}
              className={`rail-btn ${tool === t.id ? 'on' : ''}`}
              aria-expanded={tool === t.id}
              // Clicking the open tool closes it, which hands the width to the table.
              onClick={() => setTool((cur) => (cur === t.id ? null : t.id))}
            >
              <span className="rail-icon" aria-hidden="true">
                {t.icon}
              </span>
              <span className="rail-label">{t.label}</span>
              {badges[t.id] && <span className="rail-dot">{badges[t.id]}</span>}
            </button>
          ))}
        </nav>

        {tool && <aside className="dock">{panels[tool]}</aside>}

        <main className="main">
          <ScopeTrail
            dataset={dataset}
            operators={meta.operators}
            applied={applied}
            onDropFilter={dropFilter}
            dupApplied={dupApplied}
            onDropDup={() => setDupApplied(null)}
            compareApplied={compareApplied}
            onDropCompare={() => setCompareApplied(null)}
            insightApplied={insightApplied}
            onDropInsight={() => setInsightApplied(null)}
            status={status}
            resolutionNote={resolutionNote}
            viewNote={viewNote}
            onDismissNote={() => setViewNote(null)}
            maxRetained={meta.maxRetained}
          />

          <TableStats
            dataset={dataset}
            status={status}
            dupApplied={dupApplied}
            compareApplied={compareApplied}
            insightApplied={insightApplied}
            filtered={appliedRules > 0}
          />

          {insightApplied ? (
            <>
              {insightError && <div className="alert insight-alert">{insightError}</div>}
              <InsightsView
                result={insightResult}
                columns={insightApplied.columns}
                scanning={insightResult != null && !insightResult.done}
                scopeLabel={scopeLabel}
              />
            </>
          ) : (
            <DataTable
              columns={tableColumns}
              widths={widths}
              getRow={getRowView}
              rowCount={rowCount}
              ensureRange={ensureRange}
              done={status.done}
              // The diff layout: a second row-number column for the compared
              // file, a status column, and changed cells showing both values.
              compare={!!compareApplied}
              // Unique-only rows have no groups to show; deduplicated ones do —
              // the count says how many copies that row now stands for. A
              // comparison lists rows from two files, where a group number from
              // one of them would mean nothing.
              showGroups={!compareApplied && !!dupApplied && dupApplied.mode !== 'unique'}
              // Only these two list every copy, so only they have blocks to draw.
              groupBlocks={!compareApplied && (dupApplied?.mode === 'duplicates' || dupApplied?.mode === 'dropped')}
              // Which copy wins is only a question where every copy is listed.
              showKeep={!compareApplied && dupApplied?.mode === 'duplicates'}
              // Where each listed row stands for a whole group, the count is
              // how many copies it speaks for rather than which group it is in.
              groupLabel={dupApplied?.mode === 'dedupe' || dupApplied?.mode === 'kept' ? 'copies' : 'group'}
              emptyMessage={
                compareInfo
                  ? compareInfo.empty
                  : dupApplied
                    ? dupMode(dupApplied.mode).empty
                    : 'No rows match these filters.'
              }
              view={`${specSignature(applied)}|${JSON.stringify(dupApplied)}|${JSON.stringify(compareApplied)}`}
            />
          )}
        </main>
      </div>
    </div>
  )
}
